import type { AnyAction, Middleware } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import { AUTH_REDUCER_KEY } from 'app-components/flux/auth';
import { getLogger, localPreferences } from 'app-components/utils/core';
import {
  asyncDispatchMiddleware,
  crashReporterMiddleware,
  inject,
  sagaMiddleware,
} from 'app-components/utils/redux-store';

import { loadAuthSession } from 'utils/auth/storage';

import reducer from './reducers';
import { rootSaga } from './sagas';
import { initListeners, listenerMiddleware } from './storeListeners';

const logger = getLogger('store.js');

const HIDDEN_DEVTOOLS_ACTIONS = new Set<string>([
  'aiChatStore/appendStreamingDelta',
  'aiChatStore/appendReasoningDelta',
]);

const actionLoggerMiddleware: Middleware = (_store) => (next) => (action: AnyAction) => {
  logger.debug('action: ', action.type, action.meta);
  return next(action);
};

export const persistedAuthSession = loadAuthSession();

export function createStore(enableSagas = false, preloadedState = {}) {
  if (persistedAuthSession) {
    // eslint-disable-next-line no-param-reassign
    // @ts-expect-error FIXME TS7053
    preloadedState[AUTH_REDUCER_KEY] = persistedAuthSession;
  }
  const store = configureStore({
    preloadedState,
    reducer,
    middleware(getDefaultMiddleware) {
      const middleware = getDefaultMiddleware({ serializableCheck: false, immutableCheck: false });
      middleware.push(crashReporterMiddleware); // crashReporterMiddleware need to be first
      middleware.push(asyncDispatchMiddleware);
      middleware.push(actionLoggerMiddleware);
      middleware.push(listenerMiddleware.middleware);
      if (enableSagas) {
        middleware.push(sagaMiddleware);
      }
      return middleware;
    },
    devTools: localPreferences.getDebug()
      ? {
          trace: true,
          traceLimit: 20,
          predicate: (_state: unknown, action: AnyAction) => !HIDDEN_DEVTOOLS_ACTIONS.has(action.type),
        }
      : false,
  });

  inject(store as any);

  if (enableSagas) {
    sagaMiddleware.run(rootSaga);
  }

  logger.info('Dispatching initListeners...');
  store.dispatch(initListeners());
  logger.info('Store initialization complete');

  return store;
}

export type RootStore = ReturnType<typeof createStore>;
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<RootStore['getState']>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = RootStore['dispatch'];
